import { Route } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SurfaceGate } from "@/components/SurfaceGate";
// CANARY — makes CBAM_RESEARCH_PROPRIETARY reachable from (only) the internal graph. See the marker.
// Referenced (not just side-effect-imported) below so the string literal is never tree-shaken away.
import { __RESEARCH_MODULE_MARKER__ } from "@/pages/research/researchModuleMarker";
// Research ops console
import ResearchLayout from "@/pages/research/ResearchLayout";
import ResearchHome from "@/pages/research/ResearchHome";
import ResearchList from "@/pages/research/ResearchList";
import ResearchDetail from "@/pages/research/ResearchDetail";
import ResearchEditor from "@/pages/research/ResearchEditor";
import ResearchManager from "@/pages/research/ResearchManager";
import ResearchTaxonomy from "@/pages/research/ResearchTaxonomy";
import ResearchPrompts from "@/pages/research/ResearchPrompts";
import ResearchCosts from "@/pages/research/ResearchCosts";
import ResearchKG from "@/pages/research/ResearchKG";
import ResearchKGNode from "@/pages/research/ResearchKGNode";
import ResearchIngest from "@/pages/research/ResearchIngest";
import ResearchSources from "@/pages/research/ResearchSources";
import ResearchEvals from "@/pages/research/ResearchEvals";
import ResearchEvalRuns from "@/pages/research/ResearchEvalRuns";
import ResearchCapsules from "@/pages/research/ResearchCapsules";
import ResearchCapsuleDetail from "@/pages/research/ResearchCapsuleDetail";
import PhasePlaceholder from "@/pages/research/PhasePlaceholder";
import ResearchTriage from "@/pages/research/ResearchTriage";
import ResearchFred from "@/pages/research/ResearchFred";
import ResearchMemory from "@/pages/research/ResearchMemory";
import ResearchPredictions from "@/pages/research/ResearchPredictions";
import ResearchDrift from "@/pages/research/ResearchDrift";
import ResearchSuggestions from "@/pages/research/ResearchSuggestions";
import WatchRulesList from "@/pages/research/watch-rules/WatchRulesList";
import WatchRuleEditor from "@/pages/research/watch-rules/WatchRuleEditor";
import WatchRuleRuns from "@/pages/research/watch-rules/WatchRuleRuns";
import ResearchConnectors from "@/pages/research/connectors/ResearchConnectors";
import ResearchConnectorsCallback from "@/pages/research/connectors/ResearchConnectorsCallback";
import ResearchChatLayout from "@/pages/research/chat/ResearchChatLayout";
import ResearchChatIndex from "@/pages/research/chat/ResearchChatIndex";
import ResearchChatSession from "@/pages/research/chat/ResearchChatSession";
import ResearchChatMemory from "@/pages/research/chat/ResearchChatMemory";
import HouseViewFeed from "@/pages/research/HouseViewFeed";
import HouseViewDetail from "@/pages/research/HouseViewDetail";
import ResearchDebateTrace from "@/pages/research/ResearchDebateTrace";
import ResearchUrlBlacklist from "@/pages/research/ResearchUrlBlacklist";
// Research Hub — reader shell
import ResearchHubLayout, { ResearchHubIndex } from "@/pages/research-hub/ResearchHubLayout";
import CioToday from "@/pages/research-hub/CioToday";
import CioThesisHealth from "@/pages/research-hub/CioThesisHealth";
import CioConflicts from "@/pages/research-hub/CioConflicts";
import CioCoverage from "@/pages/research-hub/CioCoverage";
import CioCalibration from "@/pages/research-hub/CioCalibration";
import ThesisDetail from "@/pages/research-hub/ThesisDetail";
import PmBook from "@/pages/research-hub/PmBook";
import PmPositioning from "@/pages/research-hub/PmPositioning";
import AnalystWorkbench from "@/pages/research-hub/AnalystWorkbench";
import ScenarioTrace from "@/pages/research-hub/ScenarioTrace";
import Divergence from "@/pages/research-hub/Divergence";
import CohortDivergence from "@/pages/research-hub/CohortDivergence";
import ResearchAskChat from "@/pages/research-hub/ResearchAskChat";
import MyBriefs from "@/pages/research-hub/MyBriefs";
import MeetingNotes from "@/pages/research-hub/MeetingNotes";
import UserMemory from "@/pages/research-hub/UserMemory";
// Mobile research-visibility pages
import MobileResearch from "@/pages/mobile/MobileResearch";
import MobileResearchDetail from "@/pages/mobile/MobileResearchDetail";
import MobileOutputs from "@/pages/mobile/MobileOutputs";
import MobilePredictions from "@/pages/mobile/MobilePredictions";
import MobileKnowledgeGraph from "@/pages/mobile/MobileKnowledgeGraph";

// PROPRIETARY research route tree — the KG / RAG / predictions / evals / house-view surfaces.
//
// AUDIENCE-SEPARATION (Phase 4 build-wall): this fragment is imported by the INTERNAL entry ONLY
// (src/app/AppInternal.tsx). AppAdviser / AppPublic never name this module, so Rollup never reads
// it when building those targets — the entire research surface (and its route strings, RPC names,
// UI copy and the CBAM_RESEARCH_PROPRIETARY canary) is physically absent from dist/adviser and
// dist/public. This is import-graph exclusion, not tree-shaking. Never let an adviser/public route
// tree import this file — the ESLint no-restricted-imports rule + dependency-cruiser guard against
// exactly that, and the build-wall Vitest fails if any token here reaches those bundles.
export function researchRoutes() {
  return (
    <>
      {/* CANARY route — never navigated to, but references __RESEARCH_MODULE_MARKER__ in rendered
          output so the CBAM_RESEARCH_PROPRIETARY string literal survives minification/tree-shaking
          and is a reliable positive-control probe in dist/internal. */}
      <Route
        path="/__research_module_marker__"
        element={<span hidden data-marker={__RESEARCH_MODULE_MARKER__} />}
      />
      <Route
        path="/partner/research"
        element={
          <ProtectedRoute requiredTeam="Investment">
            <Layout>
              <ResearchLayout />
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route index element={<ResearchHome />} />
        <Route path="items" element={<ResearchList />} />
        <Route path="new" element={<ResearchEditor />} />
        <Route path="manager" element={<ResearchManager />} />
        <Route path="taxonomy" element={<ResearchTaxonomy />} />
        <Route path="prompts" element={<ResearchPrompts />} />
        <Route path="costs" element={<ResearchCosts />} />
        <Route path="kg" element={<ResearchKG />} />
        <Route path="kg/:nodeId" element={<ResearchKGNode />} />
        <Route path="ingest" element={<ResearchIngest />} />
        <Route path="sources" element={<ResearchSources />} />
        <Route path="evals" element={<ResearchEvals />} />
        <Route path="evals/runs" element={<ResearchEvalRuns />} />
        <Route path="capsules" element={<ResearchCapsules />} />
        <Route path="capsules/:id" element={<ResearchCapsuleDetail />} />
        <Route path="drafts" element={<ResearchList defaultStatus="draft" title="Drafts review queue" description="Approve, edit or reject draft research items before they progress to in-review and publication." />} />
        <Route path="fred" element={<ResearchFred />} />
        <Route path="predictions" element={<ResearchPredictions />} />
        <Route path="watch-rules" element={<WatchRulesList />} />
        <Route path="watch-rules/new" element={<WatchRuleEditor />} />
        <Route path="watch-rules/:id" element={<WatchRuleEditor />} />
        <Route path="watch-rules/:id/runs" element={<WatchRuleRuns />} />
        <Route path="connectors" element={<ResearchConnectors />} />
        <Route path="connectors/callback" element={<ResearchConnectorsCallback />} />
        <Route path="memory" element={<ResearchMemory />} />
        <Route path="triage" element={<ResearchTriage />} />
        {/* Suggestions inbox — MUST stay above the `:id` catch-all below, or
            `/suggestions` would resolve to <ResearchDetail id="suggestions"/>. */}
        <Route path="suggestions" element={<ResearchSuggestions />} />
        <Route path="drift" element={<ResearchDrift />} />
        <Route path="chat" element={<ResearchChatLayout />}>
          <Route index element={<ResearchChatIndex />} />
          <Route path="memory" element={<ResearchChatMemory />} />
          <Route path=":sessionId" element={<ResearchChatSession />} />
        </Route>
        <Route path="publication" element={<PhasePlaceholder phase={7} title="Partner publication portal" description="Externally-facing publication portal for approved research." />} />
        <Route path="admin/url-blacklist" element={<ResearchUrlBlacklist />} />
        <Route path=":id" element={<ResearchDetail />} />
        <Route path=":id/edit" element={<ResearchEditor />} />
        <Route path=":id/debate" element={<ResearchDebateTrace />} />
        <Route path="items/:id/debate" element={<ResearchDebateTrace />} />
      </Route>
      {/* Research Hub — reader shell. Sibling to /partner/research (avoids its :id
          catch-all). Gated by has_research_capability('read') in the layout and by
          has_surface_access per role section (via <SurfaceGate>). */}
      <Route
        path="/partner/research-hub"
        element={
          <ProtectedRoute>
            <Layout>
              <ResearchHubLayout />
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route index element={<ResearchHubIndex />} />
        <Route path="today" element={<SurfaceGate surface="cio"><CioToday /></SurfaceGate>} />
        <Route path="today/views" element={<SurfaceGate surface="cio"><CioThesisHealth /></SurfaceGate>} />
        <Route path="today/conflicts" element={<SurfaceGate surface="cio"><CioConflicts /></SurfaceGate>} />
        <Route path="today/coverage" element={<SurfaceGate surface="cio"><CioCoverage /></SurfaceGate>} />
        <Route path="today/calibration" element={<SurfaceGate surface="cio"><CioCalibration /></SurfaceGate>} />
        <Route path="today/tensions" element={<SurfaceGate surface="cio"><Divergence /></SurfaceGate>} />
        <Route path="book" element={<SurfaceGate surface="pm"><PmBook /></SurfaceGate>} />
        <Route path="book/positioning" element={<SurfaceGate surface="pm"><PmPositioning /></SurfaceGate>} />
        <Route path="workbench" element={<SurfaceGate surface="analyst"><AnalystWorkbench /></SurfaceGate>} />
        <Route path="workbench/scenario" element={<SurfaceGate surface="analyst"><ScenarioTrace /></SurfaceGate>} />
        <Route path="workbench/coverage" element={<SurfaceGate surface="analyst"><CioCoverage /></SurfaceGate>} />
        <Route path="workbench/divergence" element={<SurfaceGate surface="analyst"><Divergence /></SurfaceGate>} />
        <Route path="workbench/cohort" element={<SurfaceGate surface="analyst"><CohortDivergence /></SurfaceGate>} />
        <Route path="thesis/:edgeId" element={<ThesisDetail />} />
        <Route path="ask" element={<ResearchAskChat />} />
        <Route path="briefs" element={<MyBriefs />} />
        <Route path="house-view" element={<SurfaceGate surface="cio"><HouseViewFeed /></SurfaceGate>} />
        <Route path="meeting-notes" element={<MeetingNotes />} />
        <Route path="memory" element={<UserMemory />} />
      </Route>
      <Route
        path="/partner/house-view"
        element={
          <ProtectedRoute>
            <Layout><HouseViewFeed /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/house-view/:id"
        element={
          <ProtectedRoute>
            <Layout><HouseViewDetail /></Layout>
          </ProtectedRoute>
        }
      />
      {/* Mobile research-visibility routes (research browse/detail, outputs, predictions, KG). */}
      <Route
        path="/partner/m/research"
        element={
          <ProtectedRoute>
            <Layout>
              <MobileResearch />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/m/research/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <MobileResearchDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/m/outputs"
        element={
          <ProtectedRoute>
            <Layout>
              <MobileOutputs />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/m/predictions"
        element={
          <ProtectedRoute>
            <Layout>
              <MobilePredictions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/m/kg"
        element={
          <ProtectedRoute>
            <Layout>
              <MobileKnowledgeGraph />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/m/kg/:nodeId"
        element={
          <ProtectedRoute>
            <Layout>
              <MobileKnowledgeGraph />
            </Layout>
          </ProtectedRoute>
        }
      />
    </>
  );
}
